import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ELinkButtonColorVariant,
  ELinkButtonVariant,
  EModalFooterVariants,
  ETagVariant,
  ETextStyleVariant,
  ETooltipVariant,
  Heading,
  LinkButton,
  Modal,
  Tag,
  Text,
  Tooltip,
} from '@outdoorsyco/bonfire';
import NextLink from 'next/link';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { EListingTileVariant, ListingTile } from '@/components/ui/ListingTile';
import { useRental } from '@/hooks/useRental';
import { IFavorite } from '@/redux/modules/wishlists';
import { getUserFavorites } from '@/redux/selectors/favorites';
import type { IBidMatch } from '@/services/types/bids/bids';
import { getOptimisticFavoriteCount } from '@/utility/getOptimisticFavoriteCount';
import type { IRentalTile } from '@/utility/mapSearchResultToTile';
import { mapRentalToPrice } from '@/utility/mapSearchResultToTile';
import { getVehicleTypeDisplay } from '@/utility/rentals';

interface IBidMatchCardProps {
  match: IBidMatch;
  from: string;
  to: string;
  onDecline: (matchId: number) => void | Promise<void>;
  onRequestToBook: (matchId: number, rentalId: number, instantBook: boolean) => Promise<void>;
}

const getMatchLabel = (
  score: number | null,
  intl: ReturnType<typeof useIntl>,
): { label: string; variant: ETagVariant; sentence: string } | null => {
  if (score === null || score === undefined) return null;

  if (score >= 140) {
    return {
      label: intl.formatMessage({
        id: 'EhkAwX',
        defaultMessage: 'Excellent Match',
        description: 'Trips > Smart Match > Badge label for score 140-170',
      }),
      variant: ETagVariant.Success,
      sentence: intl.formatMessage({
        id: 'CbLy4U',
        defaultMessage: 'A top match based on your preferences and location.',
        description: 'Trips > Smart Match > Description for score 140-170',
      }),
    };
  }

  if (score >= 100) {
    return {
      label: intl.formatMessage({
        id: 'L7kdBY',
        defaultMessage: 'Great Match',
        description: 'Trips > Smart Match > Badge label for score 100-139',
      }),
      variant: ETagVariant.Success,
      sentence: intl.formatMessage({
        id: 'Y5hRtw',
        defaultMessage: 'A strong match based on your preferences and location.',
        description: 'Trips > Smart Match > Description for score 100-139',
      }),
    };
  }

  if (score >= 60) {
    return {
      label: intl.formatMessage({
        id: 'ughGn6',
        defaultMessage: 'Good Match',
        description: 'Trips > Smart Match > Badge label for score 60-99',
      }),
      variant: ETagVariant.Gray,
      sentence: intl.formatMessage({
        id: '2mmgyF',
        defaultMessage: 'A solid option worth considering for your trip.',
        description: 'Trips > Smart Match > Description for score 60-99',
      }),
    };
  }

  if (score >= 30) {
    return {
      label: intl.formatMessage({
        id: 'QaxnxD',
        defaultMessage: 'Worth a Look',
        description: 'Trips > Smart Match > Badge label for score 30-59',
      }),
      variant: ETagVariant.Gray,
      sentence: intl.formatMessage({
        id: 'bVYns4',
        defaultMessage: 'This option might work for your trip.',
        description: 'Trips > Smart Match > Description for score 30-59',
      }),
    };
  }

  return null;
};

const BidMatchCard: React.FC<IBidMatchCardProps> = ({
  match,
  from,
  to,
  onDecline,
  onRequestToBook,
}) => {
  const intl = useIntl();
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const { data: rentalResponse, isLoading } = useRental(match.rental_id);
  const rental = rentalResponse?.data;
  const favorites = useSelector(getUserFavorites);
  const foundFavorite = favorites?.rentals.find((fav: IFavorite) => fav.item_id === rental?.id);
  const isFavorite = !!foundFavorite;

  const handleDeclineConfirm = useCallback(async () => {
    setIsDeclining(true);
    try {
      await onDecline(match.id);
    } finally {
      setIsDeclining(false);
      setShowDeclineModal(false);
    }
  }, [match.id, onDecline]);

  const instantBook = !!(rental?.active_options?.instant_book ?? rental?.instant_book);

  const handleRequestToBook = useCallback(async () => {
    if (!rental) return;
    setIsStartingCheckout(true);
    try {
      await onRequestToBook(match.id, rental.id, instantBook);
    } finally {
      setIsStartingCheckout(false);
    }
  }, [match.id, rental, instantBook, onRequestToBook]);

  if (isLoading || !rental) {
    const loadingTile: IRentalTile = { loading: true };
    return (
      <div data-testid="bid-match-card">
        <ListingTile rentalTile={loadingTile} variant={EListingTileVariant.Vertical} />
      </div>
    );
  }

  const imageUrls = rental.images ? rental.images.map(image => image.url) : [];
  const primaryImage = rental.primary_image_url || imageUrls[0] || '';

  const location = rental.location
    ? `${rental.location?.city}${rental.location?.state ? `, ${rental.location?.state}` : ''}`
    : '';

  const sleeps = rental.sleeps
    ? intl.formatMessage(
        { defaultMessage: 'Sleeps {sleeps}', id: 'xJdtVO' },
        { sleeps: rental.sleeps },
      )
    : undefined;

  const seats = rental.seatbelts
    ? intl.formatMessage(
        { defaultMessage: 'Seats {seats}', id: 'D+3uGl' },
        { seats: rental.seatbelts },
      )
    : undefined;

  const basePath = rental.slug || `/rv-rental/${rental.id}`;
  const dateParams = from && to ? `?from=${from}&to=${to}` : '';
  const listingUrl = `${basePath}${dateParams}`;

  const tile: IRentalTile = {
    id: rental.id,
    title: rental.name,
    photoUrl: imageUrls.length > 1 ? imageUrls.slice(0, 7) : primaryImage,
    instantBook,
    details: {
      classText: getVehicleTypeDisplay(rental.display_vehicle_type),
      location,
      ratingNumber: rental.score,
      ratingQtd: rental.reviews_num ?? rental.score_count,
      sleeps,
      seats,
      infos: [sleeps, seats].filter(Boolean) as string[],
      year: rental.vehicle_year ? `${rental.vehicle_year}` : undefined,
      make: rental.vehicle_make,
      model: rental.vehicle_model,
    },
    price: mapRentalToPrice({ rental }),
    slug: rental.slug,
    vehicleTitle: rental.vehicle_title,
    dealer: rental.dealer,
    linkUrl: listingUrl,
    isFavorite,
    favoriteCount: getOptimisticFavoriteCount(rental, isFavorite),
  };

  const matchInfo = getMatchLabel(match.score, intl);

  return (
    <div data-testid="bid-match-card" className="flex flex-col h-full">
      <div className="relative flex-1">
        <ListingTile
          rentalTile={tile}
          variant={EListingTileVariant.Vertical}
          showFirstImageOnly={false}
          showProBadge={!!rental.dealer}
          target="_blank"
          ssrNavigation={false}
        />
        {matchInfo && (
          <div className="absolute z-10 top-3 left-3">
            <Tooltip content={matchInfo.sentence} variant={ETooltipVariant.Default}>
              <Tag variant={matchInfo.variant} label={matchInfo.label} />
            </Tooltip>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-3">
        <LinkButton
          variant={ELinkButtonVariant.Underline}
          label={intl.formatMessage({
            id: '8pEhuB',
            defaultMessage: 'Not for me',
            description: 'Trips > Smart Match > Decline bid match link',
          })}
          onClick={() => setShowDeclineModal(true)}
        />
        {match.booking_id ? (
          <NextLink href={`/trips/${match.booking_id}`} passHref legacyBehavior>
            <Button
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({
                id: 'LIQqhb',
                defaultMessage: 'View Quote',
                description: 'Trips > Smart Match > View quote button when host sent a quote',
              })}
            />
          </NextLink>
        ) : match.conversation_id ? (
          <NextLink href={`/messages/${match.conversation_id}`} passHref legacyBehavior>
            <Button
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({
                id: 'RCSHA/',
                defaultMessage: 'View Message',
                description: 'Trips > Smart Match > View message button when conversation exists',
              })}
            />
          </NextLink>
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant={EButtonColorVariant.Tertiary}
              label={intl.formatMessage({
                id: 'pX/yzB',
                defaultMessage: 'View Details',
                description: 'Trips > Smart Match > View listing button',
              })}
              onClick={() => window.open(listingUrl, '_blank')}
            />
            <Button
              variant={EButtonColorVariant.Primary}
              label={
                instantBook
                  ? intl.formatMessage({ defaultMessage: 'Book now', id: '1mLe+3' })
                  : intl.formatMessage({
                      id: 'F6+1at',
                      defaultMessage: 'Request to book',
                      description: 'Trips > Smart Match > Start checkout for a matched RV',
                    })
              }
              onClick={handleRequestToBook}
              loading={isStartingCheckout}
              disabled={isStartingCheckout}
            />
          </div>
        )}
      </div>

      <Modal isOpen={showDeclineModal} onClose={() => setShowDeclineModal(false)}>
        <Modal.Actions />
        <Modal.Header>
          <Heading variant={EHeadingStyleVariant.H6}>
            {intl.formatMessage({
              id: 'H31kc9',
              defaultMessage: 'Decline this match?',
              description: 'Trips > Smart Match > Decline confirmation modal title',
            })}
          </Heading>
        </Modal.Header>
        <Modal.Content className="pb-4">
          <Text variant={ETextStyleVariant.MediumRegular}>
            {intl.formatMessage({
              id: 'eL9K+1',
              defaultMessage:
                "This match will be removed from your list. You won't be able to undo this action.",
              description: 'Trips > Smart Match > Decline confirmation modal description',
            })}
          </Text>
        </Modal.Content>
        <Modal.Divider />
        <Modal.Footer variant={EModalFooterVariants.Actions}>
          <LinkButton
            variant={ELinkButtonVariant.Underline}
            colorVariant={ELinkButtonColorVariant.Critical}
            label={intl.formatMessage({
              id: 'ttgAqZ',
              defaultMessage: 'Decline',
              description: 'Trips > Smart Match > Decline confirmation modal decline button',
            })}
            onClick={handleDeclineConfirm}
            disabled={isDeclining}
            className="!text-red-500 hover:!text-red-600 active:!text-red-700"
          />
          <Button
            variant={EButtonColorVariant.Tertiary}
            label={intl.formatMessage({
              id: 'QSUzdv',
              defaultMessage: 'Keep it',
              description: 'Trips > Smart Match > Decline confirmation modal keep button',
            })}
            onClick={() => setShowDeclineModal(false)}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BidMatchCard;
